import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { 
  HelpCircle, 
  BookOpen, 
  CheckCircle, 
  AlertTriangle, 
  Target,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  MessageSquare,
  Star,
  AlertCircle,
  TrendingUp
} from 'lucide-react';

const GuiaPreenchimento = () => {
  const [openSections, setOpenSections] = useState({
    metodologia: true,
    reconhecimento: false,
    construtivo: false,
    critico: false
  });

  const toggleSection = (section) => {
    setOpenSections({
      ...openSections,
      [section]: !openSections[section]
    });
  };

  const categorias = [
    { 
      nome: 'Técnico', 
      descricao: 'Habilidades técnicas, conhecimento de ferramentas, qualidade do código/entrega',
      exemplos: ['Domínio de tecnologias', 'Qualidade técnica', 'Inovação tecnológica']
    },
    { 
      nome: 'Comportamental', 
      descricao: 'Atitudes, iniciativa, proatividade, responsabilidade, autonomia',
      exemplos: ['Proatividade', 'Responsabilidade', 'Autonomia', 'Iniciativa']
    },
    { 
      nome: 'Comunicação', 
      descricao: 'Clareza na comunicação, escuta ativa, feedback, apresentações',
      exemplos: ['Comunicação clara', 'Escuta ativa', 'Apresentações eficazes']
    },
    { 
      nome: 'Processos & Wrike', 
      descricao: 'Seguimento de processos, uso correto de ferramentas, organização',
      exemplos: ['Uso do Wrike', 'Seguimento de processos', 'Organização']
    },
    { 
      nome: 'Projetos', 
      descricao: 'Gestão de projetos, cumprimento de prazos, qualidade das entregas',
      exemplos: ['Gestão de cronograma', 'Qualidade das entregas', 'Coordenação']
    },
    { 
      nome: 'Liderança & Equipe', 
      descricao: 'Capacidade de liderar, trabalho em equipe, mentoria, influência',
      exemplos: ['Liderança', 'Trabalho em equipe', 'Mentoria', 'Influência positiva']
    }
  ];

  const dicasPorTipo = {
    reconhecimento: {
      titulo: 'Feedback de Reconhecimento',
      icone: Star,
      cor: 'green',
      objetivo: 'Valorizar comportamentos positivos e bons resultados para reforçar e perpetuar essas práticas.',
      estrutura: [
        { etapa: 'Contexto', descricao: 'Descreva quando e onde o comportamento aconteceu' },
        { etapa: 'Observação', descricao: 'Relate especificamente o que a pessoa fez' },
        { etapa: 'Impacto', descricao: 'Explique o resultado positivo gerado' },
        { etapa: 'Next Step', descricao: 'Incentive a continuidade e expansão da prática' }
      ],
      exemplo: {
        contexto: 'Durante a apresentação para o cliente ABC na última terça-feira, você foi responsável por demonstrar a nova funcionalidade do sistema.',
        observacao: 'Você preparou uma demonstração clara e interativa, antecipou possíveis dúvidas do cliente e respondeu todas as perguntas de forma didática e confiante.',
        impacto: 'Isso gerou uma confiança imediata no cliente, que aprovou a funcionalidade na mesma reunião e elogiou nossa capacidade técnica.',
        nextStep: 'Continue aplicando essa abordagem preparatória e didática em outras apresentações. Considere compartilhar suas técnicas com outros membros da equipe.'
      }
    },
    construtivo: {
      titulo: 'Feedback Construtivo',
      icone: TrendingUp,
      cor: 'yellow',
      objetivo: 'Apoiar o desenvolvimento e melhoria contínua, oferecendo direcionamento para o crescimento profissional.',
      estrutura: [
        { etapa: 'Contexto', descricao: 'Estabeleça um ambiente seguro e o momento específico' },
        { etapa: 'Observação', descricao: 'Descreva o comportamento de forma objetiva, sem julgamentos' },
        { etapa: 'Impacto', descricao: 'Explique as consequências e oportunidades de melhoria' },
        { etapa: 'Next Step', descricao: 'Defina ações claras e ofereça suporte' }
      ],
      exemplo: {
        contexto: 'Na reunião de alinhamento do projeto XYZ na segunda-feira, discutimos o cronograma das próximas entregas.',
        observacao: 'Notei que você ficou em silêncio durante a discussão sobre os riscos técnicos, mesmo quando o assunto estava relacionado à sua área de expertise.',
        impacto: 'Isso fez com que perdêssemos a oportunidade de identificar potenciais problemas antecipadamente, o que pode impactar o cronograma do projeto.',
        nextStep: 'Vamos agendar um 1:1 para discutir formas de você se sentir mais confortável para compartilhar sua expertise em reuniões. Sua contribuição técnica é muito valiosa para a equipe.'
      }
    },
    critico: {
      titulo: 'Feedback Crítico',
      icone: AlertTriangle,
      cor: 'red',
      objetivo: 'Abordar situações que requerem mudança imediata, estabelecendo expectativas claras e consequências.',
      estrutura: [
        { etapa: 'Contexto', descricao: 'Estabeleça a seriedade da situação' },
        { etapa: 'Observação', descricao: 'Seja direto e específico sobre o comportamento problemático' },
        { etapa: 'Impacto', descricao: 'Explique as consequências sérias para equipe/projeto/cliente' },
        { etapa: 'Next Step', descricao: 'Defina mudanças obrigatórias, prazos e próximos passos' }
      ],
      exemplo: {
        contexto: 'Nos últimos três projetos (X, Y e Z), identificamos problemas recorrentes na qualidade das entregas que chegaram ao cliente.',
        observacao: 'Os testes não estão sendo executados conforme o processo definido, resultando em bugs críticos em produção que poderiam ter sido evitados.',
        impacto: 'Isso compromete nossa credibilidade com o cliente, gera retrabalho para toda a equipe e coloca em risco a renovação do contrato.',
        nextStep: 'É necessário retomar imediatamente o processo de testes estabelecido. Vamos revisar juntos o checklist na sexta-feira e você precisa me reportar o status dos testes diariamente pelas próximas duas semanas.'
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-white text-2xl font-bold">Guia de Preenchimento</h2>
        <p className="text-brand-gray">Metodologia COIN/SBI, exemplos práticos e orientações para feedbacks eficazes</p>
      </div>

      <Tabs defaultValue="metodologia" className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-brand-dark-light">
          <TabsTrigger value="metodologia" className="data-[state=active]:bg-brand-primary data-[state=active]:text-black">
            Metodologia
          </TabsTrigger>
          <TabsTrigger value="exemplos" className="data-[state=active]:bg-brand-primary data-[state=active]:text-black">
            Exemplos
          </TabsTrigger>
          <TabsTrigger value="categorias" className="data-[state=active]:bg-brand-primary data-[state=active]:text-black">
            Categorias
          </TabsTrigger>
          <TabsTrigger value="dicas" className="data-[state=active]:bg-brand-primary data-[state=active]:text-black">
            Dicas Práticas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metodologia" className="space-y-6">
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Metodologia COIN/SBI
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="bg-brand-blue/20 border-brand-blue/30">
                <HelpCircle className="h-4 w-4" />
                <AlertDescription className="text-brand-blue">
                  A metodologia COIN (Contexto, Observação, Impacto, Next Step) é uma estrutura que ajuda a dar feedbacks específicos, 
                  objetivos e acionáveis. Também conhecida como SBI (Situation, Behavior, Impact).
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-brand-dark-lighter border-brand-gray/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-brand-blue rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">C</span>
                      </div>
                      <h4 className="text-white font-medium">Contexto</h4>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white text-sm">
                      <strong>Quando e onde</strong> a situação aconteceu. Estabeleça o cenário específico.
                    </p>
                    <div className="mt-3 p-2 bg-brand-dark rounded text-xs text-white">
                      "Na reunião de ontem com o cliente..."
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-brand-dark-lighter border-brand-gray/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center">
                        <span className="text-black text-sm font-bold">O</span>
                      </div>
                      <h4 className="text-white font-medium">Observação</h4>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white text-sm">
                      <strong>O que</strong> você observou especificamente. Fatos, não interpretações.
                    </p>
                    <div className="mt-3 p-2 bg-brand-dark rounded text-xs text-white">
                      "Você respondeu todas as perguntas técnicas..."
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-brand-dark-lighter border-brand-gray/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-warning rounded-full flex items-center justify-center">
                        <span className="text-black text-sm font-bold">I</span>
                      </div>
                      <h4 className="text-white font-medium">Impacto</h4>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white text-sm">
                      <strong>Qual foi</strong> o resultado ou consequência do comportamento observado.
                    </p>
                    <div className="mt-3 p-2 bg-brand-dark rounded text-xs text-white">
                      "Isso aumentou a confiança do cliente..."
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-brand-dark-lighter border-brand-gray/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">N</span>
                      </div>
                      <h4 className="text-white font-medium">Next Step</h4>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white text-sm">
                      <strong>Próximos passos</strong> específicos e acionáveis para o futuro.
                    </p>
                    <div className="mt-3 p-2 bg-brand-dark rounded text-xs text-white">
                      "Continue aplicando essa abordagem..."
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Alert className="bg-success/20/20 border-success/30">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription className="text-green-300">
                  <strong>Benefícios da metodologia:</strong> Feedbacks mais específicos, objetivos e acionáveis. 
                  Reduz defensividade e aumenta a probabilidade de mudança comportamental positiva.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="exemplos" className="space-y-6">
          {Object.entries(dicasPorTipo).map(([tipo, info]) => {
            const IconComponent = info.icone;
            const isOpen = openSections[tipo];
            
            return (
              <Card key={tipo} className="bg-brand-dark-light border-brand-gray/10">
                <Collapsible open={isOpen} onOpenChange={() => toggleSection(tipo)}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-brand-dark-lighter/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 bg-${info.cor}-500/20 rounded-lg flex items-center justify-center`}>
                            <IconComponent className={`h-5 w-5 text-${info.cor}-400`} />
                          </div>
                          <div>
                            <CardTitle className="text-white">{info.titulo}</CardTitle>
                            <p className="text-brand-gray text-sm">{info.objetivo}</p>
                          </div>
                        </div>
                        {isOpen ? (
                          <ChevronDown className="h-5 w-5 text-brand-gray" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-brand-gray" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                      {/* Estrutura */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Estrutura COIN:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                          {info.estrutura.map((etapa, index) => (
                            <div key={index} className="bg-brand-dark-lighter p-3 rounded-lg">
                              <h5 className="text-success font-medium text-sm">{etapa.etapa}</h5>
                              <p className="text-white text-xs mt-1">{etapa.descricao}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Exemplo Prático */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Exemplo Prático:</h4>
                        <div className="space-y-4">
                          <div className="bg-brand-dark-lighter p-4 rounded-lg">
                            <h5 className="text-brand-blue font-medium mb-2">Contexto</h5>
                            <p className="text-white text-sm">{info.exemplo.contexto}</p>
                          </div>
                          
                          <div className="bg-brand-dark-lighter p-4 rounded-lg">
                            <h5 className="text-success font-medium mb-2">Observação</h5>
                            <p className="text-white text-sm">{info.exemplo.observacao}</p>
                          </div>
                          
                          <div className="bg-brand-dark-lighter p-4 rounded-lg">
                            <h5 className="text-warning font-medium mb-2">Impacto</h5>
                            <p className="text-white text-sm">{info.exemplo.impacto}</p>
                          </div>
                          
                          <div className="bg-brand-dark-lighter p-4 rounded-lg">
                            <h5 className="text-brand-primary font-medium mb-2">Next Step</h5>
                            <p className="text-white text-sm">{info.exemplo.nextStep}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="categorias" className="space-y-6">
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white">Categorias de Feedback</CardTitle>
              <p className="text-brand-gray">Entenda quando usar cada categoria e veja exemplos práticos</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {categorias.map((categoria, index) => (
                  <Card key={index} className="bg-brand-dark-lighter border-brand-gray/20">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white text-lg">{categoria.nome}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-white text-sm">{categoria.descricao}</p>
                      
                      <div>
                        <h5 className="text-success text-sm font-medium mb-2">Exemplos de situações:</h5>
                        <div className="flex flex-wrap gap-1">
                          {categoria.exemplos.map((exemplo, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs border-brand-gray/30 text-white">
                              {exemplo}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dicas" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-success" />
                  Boas Práticas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-3">
                  {[
                    'Seja específico: use dados, datas e exemplos concretos',
                    'Foque no comportamento, não na personalidade',
                    'Use linguagem neutra e objetiva',
                    'Dê feedback próximo ao momento da ocorrência',
                    'Inclua tanto aspectos positivos quanto pontos de melhoria',
                    'Ofereça suporte e recursos para o desenvolvimento',
                    'Estabeleça próximos passos claros e mensuráveis'
                  ].map((dica, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                      <p className="text-white text-sm">{dica}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-error" />
                  Armadilhas a Evitar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-3">
                  {[
                    'Generalizar: evite "sempre" ou "nunca"',
                    'Fazer julgamentos pessoais ou ataques ao caráter',
                    'Dar feedback público sobre questões sensíveis',
                    'Misturar múltiplos tópicos em um único feedback',
                    'Assumir intenções sem verificar',
                    'Não dar seguimento aos acordos estabelecidos',
                    'Focar apenas nos pontos negativos'
                  ].map((armadilha, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <AlertTriangle className="h-4 w-4 text-error mt-0.5 flex-shrink-0" />
                      <p className="text-white text-sm">{armadilha}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-warning" />
                  Dicas de Linguagem
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h5 className="text-success font-medium mb-2">Use frases como:</h5>
                  <div className="space-y-1 text-sm text-white">
                    <p>"Observei que..."</p>
                    <p>"O impacto disso foi..."</p>
                    <p>"Gostaria de entender..."</p>
                    <p>"Vamos combinar que..."</p>
                    <p>"Como posso te apoiar para..."</p>
                  </div>
                </div>
                
                <div>
                  <h5 className="text-error font-medium mb-2">Evite frases como:</h5>
                  <div className="space-y-1 text-sm text-white">
                    <p>"Você sempre..."</p>
                    <p>"Você é..."</p>
                    <p>"Você deveria saber..."</p>
                    <p>"Todo mundo percebeu..."</p>
                    <p>"Obviamente..."</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="h-5 w-5 text-brand-blue" />
                  Timing e Ambiente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-3">
                  {[
                    { 
                      titulo: 'Reconhecimento', 
                      descricao: 'Pode ser público, imediato ao acontecimento' 
                    },
                    { 
                      titulo: 'Construtivo', 
                      descricao: 'Privado, em ambiente seguro, próximo ao fato' 
                    },
                    { 
                      titulo: 'Crítico', 
                      descricao: 'Privado, com tempo adequado para conversa completa' 
                    },
                    { 
                      titulo: 'Follow-up', 
                      descricao: 'Agende revisões regulares dos acordos estabelecidos' 
                    }
                  ].map((item, index) => (
                    <div key={index} className="bg-brand-dark-lighter p-3 rounded-lg">
                      <h5 className="text-brand-blue font-medium text-sm">{item.titulo}</h5>
                      <p className="text-white text-xs mt-1">{item.descricao}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GuiaPreenchimento;